<template>
  <div v-if="showDocumentSection" class="payment-documents">
    <div
      class="bg-gray-9 rounded px-2 py-3 d-flex align-items-center cursor-pointer"
      @click="onOpenCloseDocuments"
    >
      <em
        class="fad fa-chevron-circle-right collapse-button"
        :class="{ 'fa-rotate-90': visibleDocuments }"
      />
      <span class="color-gray-7 font-weight-bold">Documentos</span>
    </div>
    <b-collapse v-model="visibleDocuments">
      <div
        v-for="(document, idx) in documents"
        :key="`document-$${idx}`"
        class="bg-gray-9 rounded py-3 px-4 mt-1 row mx-0 align-items-center"
      >
        <div class="col-12 col-md-5">
          {{ document.name }}
        </div>
        <div
          class="col-12 col-md-2"
          :class="{
            'color-green': document.status,
            'color-primary-4': document.status === false,
          }"
        >
          {{ document.statusDescription }}
        </div>
        <div
          class="col-12 col-md-5 d-flex justify-content-end align-items-center p-0"
        >
          <action-button
            v-if="document.isGenerated && !document.fileId && !disabled"
            i="fas fa-redo-alt"
            text="Generar Documento"
            variant="outline-success"
            @click="generateDocument(document)"
          />
          <validation-provider v-else v-slot="{ errors }" rules="required">
            <custom-file-input
              :key="`fileUpload-$${idx}`"
              v-model="document.model"
              accept=".pdf"
              :attachment-id="document.fileId"
              :class="{ 'file-input-disabled': disabled }"
              custom-error-msg="Favor seleccionar un documento."
              :disabled="disabled"
              display-style="normal"
              :error="errors[0]"
              :name="document.filename"
              :on-download="onDownloadFile"
              required
              @input="
                onUploadPaymentDocument(
                  $event,
                  document.documentTypeId,
                  document.fileId
                )
              "
            />
          </validation-provider>
          <action-button
            v-if="document.fileId && disabled && document.status !== false"
            class="ml-3 download-btn"
            i="fas fa-file-download"
            variant="outline-success"
            @click="onDownloadFile(document.fileId)"
          />
          <action-button
            v-if="
              canResendDocument &&
                ((document.fileId && disabled && document.status === false) ||
                  permissionCanResendDocument)
            "
            i="fas fa-redo-alt"
            text="Reenviar"
            variant="outline-success ml-3"
            @click="resend(document)"
          />
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomFileInput from 'CustomFileInput';
import _generalApi from '@/modules/app/api.js';
import documentType from '@/utils/shared/constants/documentTypes.js';
import {
  getDocumentPayments,
  uploadDocumentPayment,
  deleteDocumentPayment,
  resendDocumentPayment,
} from '@/modules/billingHistory/api';
import alert from 'utils/mixins/alert.mixin';
import billingStatus from '@/utils/constants/billingStatus.js';

const DOCUMENTS_STATUS = Object.freeze({
  success: 0,
});

const DOCUMENTS = Object.freeze({
  ExcelMaster: {
    documentTypeId: documentType.ExcelMaster,
    name: 'Excel Master',
    reportType: 'excelmaster',
    status: null,
    isGenerated: true,
    model: null,
    filename: null,
    fileId: null,
  },
  InvoiceAdjustment: {
    documentTypeId: documentType.InvoiceAdjustment,
    name: 'Informe de Ajuste',
    reportType: 'invoiceadjustment',
    status: null,
    isGenerated: true,
    model: null,
    filename: null,
    fileId: null,
  },
  PaymentDistribution: {
    documentTypeId: documentType.PaymentDistribution,
    name: 'Distribución de Pago',
    status: null,
    model: null,
    filename: null,
    fileId: null,
  },
  MixedServicesCertification: {
    documentTypeId: documentType.MixedServicesCertification,
    name: 'Certificación de Servicios Mixtos',
    reportType: 'mixedservicescertification',
    needsPaymentId: true,
    status: null,
    isGenerated: false,
    model: null,
    filename: null,
    fileId: null,
    needsStatusValidation: true,
    validationStatus: [
      billingStatus.Aprobado,
      billingStatus.DevueltoTPFA,
      billingStatus.EnviadoFinanzasParcial,
    ],
    preventUpload: true,
  },
});

export default {
  name: 'PaymentDocuments',
  components: { ActionButton, CustomFileInput },
  mixins: [alert],
  props: {
    bill: {
      type: Number,
      default: () => null,
    },
    paymentId: {
      type: Number,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    voucherNumber: {
      type: String,
      default: () => null,
    },
    billStatusId: {
      type: Number,
      default: () => null,
    },
    canResendDocument: {
      type: Boolean,
      default: () => true,
    },
  },
  data: () => ({
    documents: [],
    visibleDocuments: true,
    showDocumentSection: true,
  }),
  computed: {
    permissionCanResendDocument() {
      return this.userHasPermissions('provider.resendsifdedocument');
    },
  },
  async created() {
    await this.getDocuments();
  },
  methods: {
    async getDocuments() {
      const { data } = await getDocumentPayments(this.paymentId);

      if (this.voucherNumber && data.length == 0) {
        this.showDocumentSection = false;
        return;
      }

      this.documents = Object.values(DOCUMENTS).map((document) => {
        const item = data.find(
          (d) => d.documentTypeId === document.documentTypeId
        );
        const obj = {
          ...document,
          filename: item?.filename,
          fileId: item?.documentId,
        };

        if (item?.status != null) {
          if (Number(item?.status) === DOCUMENTS_STATUS.success) {
            obj.status = true;
            obj.statusDescription = 'Enviado';
          } else if (Number(item?.status) > DOCUMENTS_STATUS.success) {
            obj.status = false;
            obj.statusDescription = 'Error';
          }
        }

        return obj;
      });
    },
    async generateDocument(document) {
      let url = `${document.reportType}/${this.bill}`;

      if (document.needsPaymentId) {
        url += `/${this.paymentId}`;
      }

      if (
        document.needsStatusValidation &&
        !document.validationStatus.includes(this.billStatusId)
      ) {
        this.ShowToast(
          'Alerta',
          'No es posible generar el documento por el estatus en el que se encuentra la factura.',
          'error'
        );
        return;
      }

      const { data } = await _generalApi.getReport(url);
      if (!data) return;

      const file = new File(
        [_generalApi.getBlobConvertion(data)],
        data.filename,
        {
          type: data.contentType,
        }
      );

      document.model = file;

      if (document.preventUpload != true) {
        this.onUploadPaymentDocument(
          document.model,
          document.documentTypeId,
          null,
          true
        );
      } else {
        this.getDocuments();
      }
    },
    async onUploadPaymentDocument(document, documentTypeId, fileId) {
      if (!document) {
        if (fileId) {
          await deleteDocumentPayment(fileId).then(() => {
            this.ShowToast(
              'Proceso Exitoso',
              'El archivo ha sido eliminado existosamente.',
              'success'
            );
            this.getDocuments();
          });
        }
        return;
      }

      let params = new FormData();
      params.append('file', document);

      await uploadDocumentPayment(
        this.bill,
        documentTypeId,
        this.paymentId,
        params
      )
        .then(() => this.getDocuments())
        .catch(() => this.ShowToast('', 'Error al subir el archivo.', 'error'));
    },
    async resend({ fileId }) {
      await resendDocumentPayment(fileId)
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso',
            'El archivo ha sido reenviado exitosamente.',
            'success'
          );
          this.getDocuments();
        })
        .catch(() =>
          this.ShowToast('', 'Error al reenviar el archivo.', 'error')
        );
    },
    onDownloadFile(documentId) {
      return _generalApi.downloadDocumentByAttachmentId(documentId);
    },
    onOpenCloseDocuments() {
      this.visibleDocuments = !this.visibleDocuments;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/shared/scss/config/_colors.scss';

.payment-documents {
  .collapse-button {
    --fa-primary-color: #{map-get($colors, 'green')};
    --fa-secondary-color: #{map-get($colors, 'white')};
    --fa-secondary-opacity: 1;
    font-size: 1.5rem;
    margin-right: 0.5rem;
    transition: transform 0.3s;
  }
  ::v-deep {
    .download-btn {
      i {
        margin: 0 !important;
      }
    }
    .file-input-disabled {
      .button-container {
        display: none !important;
      }
    }
  }
}
</style>
